import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useStatsTooltip, StatsTooltipCaret } from '../courts/SessionPlayerPill';
import {
  getPlayerNameColor,
  buildPlayerEloTooltipText,
  formatSessionGamesCountLabel,
  getRemoveFromSessionBlockReason,
  canRemovePlayerFromSession,
} from '../../utils/playerUtils';
import { getInitials } from '../../utils/nameUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import { MATCHMAKING_CONFIG } from '@badminton/matchmaking-core';
import { ConfirmationModal } from '../common';
import CompetitivePlayerOverflowMenu from './CompetitivePlayerOverflowMenu';
function hardPreferenceLongWaitExplanation(matchPreference) {
  const pref =
    matchPreference === 'prefer_mixed'
      ? 'Prefer Mixed (mixed doubles)'
      : 'Prefer Levels (same-gender doubles)';
  return `Long wait — ${pref} is set to Hard. The matchmaker only picks you when that game type can be formed from the current queue. Switch to Soft or No preference if you want to play sooner.`;
}

/** Same ratings tooltip as court / upcoming player pills (full name + games + Elos). */
function WaitlistStatsTooltipName({
  player,
  useSinglesElo,
  className,
  waitMetricSlice = null,
  showEloTooltip = true,
}) {
  const {
    showTooltip,
    setShowTooltip,
    tooltipRef,
    floatingRef,
    tooltipStyle,
    placement,
    onAnchorMouseEnter,
    onAnchorKeyboardOpen,
  } = useStatsTooltip({
    width: 300,
    estHeight: 120,
    gap: 6,
  });

  const statsTooltipStr = buildPlayerEloTooltipText(player, {
    useSinglesElo,
    eloChangeTotal: null,
    sessionWaitMetrics: waitMetricSlice,
  });
  const label = player.name || '';
  if (!showEloTooltip) {
    return (
      <span className={className} title={player.name || undefined}>
        {label}
      </span>
    );
  }

  const canHover =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const portal =
    showTooltip &&
    createPortal(
      <div
        ref={floatingRef}
        className="relative max-w-xs px-3 py-2 text-sm rounded-lg pointer-events-none border border-border-medium bg-surface-subtle text-text-primary shadow-lg dark:bg-surface-elevated dark:text-text-primary dark:shadow-xl"
        style={tooltipStyle}
      >
        <div className="font-medium whitespace-pre-line">{statsTooltipStr}</div>
        <StatsTooltipCaret placement={placement} />
      </div>,
      document.body
    );

  return (
    <>
      <span
        ref={tooltipRef}
        className={className}
        onMouseEnter={(e) => {
          onAnchorMouseEnter(e);
          if (canHover) setShowTooltip(true);
        }}
        onMouseLeave={() => canHover && setShowTooltip(false)}
        onClick={(e) => {
          e.stopPropagation();
          if (!canHover) setShowTooltip((p) => !p);
        }}
        onFocus={() => {
          onAnchorKeyboardOpen();
          setShowTooltip(true);
        }}
        onBlur={() => setShowTooltip(false)}
        tabIndex={0}
        role="button"
        aria-label={`View ratings for ${player.name}`}
      >
        {label}
      </span>
      {portal}
    </>
  );
}

function WaitlistDnDPanel({ enabled, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'waitingList', disabled: !enabled });
  return (
    <div
      ref={setNodeRef}
      className={enabled && isOver ? 'rounded-lg ring-2 ring-primary/35 ring-inset' : undefined}
    >
      {children}
    </div>
  );
}

function WaitlistPlayerDraggable({ playerId, enabled, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-waitlist-${playerId}`,
    disabled: !enabled,
    data: { playerId, from: 'waitingList' },
  });
  // Cursor-following preview is rendered via DragOverlay on the session page (compact court-sized row).
  const style = isDragging ? { opacity: 0.35 } : undefined;
  return children({
    outerRef: setNodeRef,
    style,
    isDragging,
    dragHandleProps: enabled ? { ...listeners, ...attributes } : null,
  });
}

/**
 * Reusable WaitingList component for displaying players in the waiting pool
 * @param {Object} props
 * @param {Array} props.players - Array of player IDs
 * @param {Object} props.playersById - Map of player ID to player object
 * @param {Function} props.onRemovePlayer - Callback when player is removed from waiting list
 * @param {boolean} props.hasCourtSlotAvailable - Whether there's a court slot available
 * @param {string} props.currentClubId - Current club ID
 * @param {Object} props.playerWaitMetrics - Wait metrics for court rotation tracking
 * @param {number} props.gamesMode - Mode of games played in the session
 * @param {Object} props.playerStreaks - Win/loss streaks for performance badges
 * @param {Array} props.deferredPlayers - Array of deferred player objects with id and reason
 * @param {Object} props.sessionPlayerStatus - Player status in session
 * @param {Object} props.sessionPlayerScheduledGames - Map of player ID to scheduled_game_id
 * @param {Object} props.partnershipLocks - Map of playerId -> { partnerId, reason }
 * @param {number} [props.waitGuaranteeThreshold] - Courts observed needed for "Up Soon" icon; defaults to 4. Should match session's number of courts when provided.
 * @param {boolean} [props.useSinglesElo] - competitive 1v1: show singles Elo only (not XD / Levels)
 * @param {(playerId: string) => void | Promise<void>} [props.onPausePlayer] - host control: pause (active → paused)
 * @param {(playerId: string) => void | Promise<void>} [props.onResumePlayer] - host control: resume (paused → active)
 * @param {(playerId: string) => void | Promise<boolean>} [props.onRemoveFromSession] - host: hard-remove player (zero games only)
 * @param {Object} [props.partnershipLookup] - playerId → partnerId for active session partnerships
 * @param {Set<string>} [props.playersInUpcomingCourts] - player IDs on upcoming/next_up slots
 * @param {string} [props.wrapperClassName] - outer spacing (default `mt-6`)
 * @param {boolean} [props.hostSessionRosterDnDEnabled] - host: waitlist cards draggable / panel accepts drops from courts & upcoming
 * @param {boolean} [props.matchPreferencesEnabled] - when false, hide preference-related waitlist badges (hard-pref long wait)
 * @param {boolean} [props.cooldownEnabled] - when false, hide cooldown (ice cube) badges on waitlist players
 * @param {object} [props.hostActionContext] - shared host menu context (Courts tab overflow menu)
 * @param {boolean} [props.showHostOverflowMenu] - host-only ⋯ menu on list rows
 * @param {string[]} [props.hostOverflowExcludeKeys] - menu keys to hide (duplicate row buttons)
 * @param {string|null} [props.hostMenuOpenPlayerId] - which row's ⋯ menu is open (single-open)
 * @param {(id: string|null) => void} [props.onHostMenuOpenPlayerIdChange]
 * @param {boolean} [props.showQueuePosition] - show 1-based waitlist order next to each player
 * @param {string[]} [props.queueOrderIds] - full waitlist order for positions when `players` is filtered (e.g. search)
 */
function QueuePositionMark({ position, className = 'w-5 text-xs' }) {
  if (position == null) return null;
  return (
    <span
      className={`shrink-0 text-right font-semibold tabular-nums text-text-muted ${className}`}
      aria-label={`Queue position ${position}`}
    >
      {position}
    </span>
  );
}

const WaitingList = (props) => {
  const {
    players,
    playersById = {},
    onRemovePlayer,
    hasCourtSlotAvailable,
    currentClubId = 'default',
    sessionPlayerStatus = {},
    sessionPlayerScheduledGames = {},
    playerWaitMetrics = {},
    gamesMode = 0,
    playerStreaks = {},
    deferredPlayers = [],
    partnershipLocks = {},
    waitGuaranteeThreshold = 4,
    useSinglesElo = false,
    onPausePlayer,
    onResumePlayer,
    onRemoveFromSession,
    partnershipLookup = {},
    playersInUpcomingCourts = null,
    wrapperClassName = 'mt-6',
    hostSessionRosterDnDEnabled = false,
    highlightPlayerId = null,
    matchPreferencesEnabled = true,
    cooldownEnabled = false,
    hostActionContext = null,
    showHostOverflowMenu = false,
    hostOverflowExcludeKeys = [],
    hostMenuOpenPlayerId = null,
    onHostMenuOpenPlayerIdChange,
    /** 'cards' (default) | 'chips' — compact horizontal pill per player */
    variant = 'cards',
    showQueuePosition = false,
    queueOrderIds = null,
    showEloTooltip = true,
  } = props;
  const [busyPlayerId, setBusyPlayerId] = useState(null);
  const [removeConfirmPlayer, setRemoveConfirmPlayer] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const getQueuePosition = (playerId) => {
    if (!showQueuePosition) return null;
    const order = Array.isArray(queueOrderIds) && queueOrderIds.length > 0 ? queueOrderIds : players;
    const idx = order.indexOf(playerId);
    return idx >= 0 ? idx + 1 : null;
  };

  const isInUpcomingCourt = (playerId) =>
    playersInUpcomingCourts && typeof playersInUpcomingCourts.has === 'function' && playersInUpcomingCourts.has(playerId);

  const canRemoveFromSession = (player) =>
    onRemoveFromSession &&
    canRemovePlayerFromSession({
      player,
      isInUpcoming: isInUpcomingCourt(player.id),
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
    });

  const getRemoveBlockTitle = (player) =>
    getRemoveFromSessionBlockReason({
      player,
      isInUpcoming: isInUpcomingCourt(player.id),
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
    });

  const runPauseAction = async (fn, id) => {
    if (!fn || busyPlayerId) return;
    setBusyPlayerId(id);
    try {
      await fn(id);
    } finally {
      setBusyPlayerId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeConfirmPlayer || !onRemoveFromSession) return;
    setRemoveLoading(true);
    try {
      const success = await onRemoveFromSession(removeConfirmPlayer.id);
      if (success !== false) {
        setRemoveConfirmPlayer(null);
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  const renderRemoveButton = (player, { compact = false } = {}) => {
    if (!onRemoveFromSession) return null;
    const blockReason = getRemoveBlockTitle(player);
    if (blockReason) return null;
    const sizeClass = compact ? 'h-5 w-5' : 'h-5 w-5';
    const iconClass = compact ? 'h-2.5 w-2.5' : 'h-2.5 w-2.5';
    return (
      <button
        type="button"
        title={`Remove ${player.name} from session`}
        aria-label={`Remove ${player.name} from session`}
        disabled={busyPlayerId !== null || removeLoading}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setRemoveConfirmPlayer(player)}
        className={`flex ${sizeClass} items-center justify-center rounded-full border border-border-medium bg-surface text-text-muted transition hover:border-danger/60 hover:bg-danger-light/30 hover:text-danger-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-50`}
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  };

  const removeConfirmModal = (
    <ConfirmationModal
      isOpen={!!removeConfirmPlayer}
      onClose={() => setRemoveConfirmPlayer(null)}
      onConfirm={handleConfirmRemove}
      title="Remove from session"
      message={
        removeConfirmPlayer
          ? `Remove ${removeConfirmPlayer.name} from this session? They will return to the available list.`
          : ''
      }
      confirmText="Remove"
      isDangerous
      loading={removeLoading}
    />
  );

  if (!players || players.length === 0) {
    return null;
  }

  // Function to determine wait priority based on games played and court rotations
  const getWaitPriority = (player) => {
    const effectiveGames = player.effectiveGamesPlayed !== undefined ? player.effectiveGamesPlayed : (player.gamesPlayedThisSession || 0);
    const gamesDeficit = gamesMode - effectiveGames;
    const courtsObserved = playerWaitMetrics[player.id]?.courtsObserved || 0;
    
    // Show hourglass if player meets greedy selection criteria:
    // 1. At least 1 game behind mode, OR
    // 2. At mode but observed waitGuaranteeThreshold+ courts (patience reward; threshold = session court count when provided)
    const isGreedy = gamesDeficit >= 1 || (gamesDeficit >= 0 && gamesDeficit < 1 && courtsObserved >= waitGuaranteeThreshold);
    if (isGreedy) return 'UP_SOON';
    return 'NORMAL';
  };

  const isFairnessBlocked = (player) => {
    if (!gamesMode && gamesMode !== 0) return false;
    const effectiveGames = player.effectiveGamesPlayed !== undefined ? player.effectiveGamesPlayed : (player.gamesPlayedThisSession || 0);
    return effectiveGames >= gamesMode + 2;
  };
  
  // Function to get performance badge based on consecutive win streaks
  const getPerformanceBadge = (player) => {
    const streak = playerStreaks[player.id];
    if (!streak) return null;
    const consecutive = streak.currentWinStreak ?? 0;
    if (consecutive >= 5) return { icon: '💎', tooltip: 'Elite Form: 5+ consecutive wins' };
    if (consecutive >= 3) return { icon: '🔥', tooltip: 'Hot Streak: 3+ consecutive wins' };
    return null;
  };
  
  // Function to check if player is deferred for better matchup
  const isDeferredPlayer = (playerId) => {
    return deferredPlayers?.some(player => player.id === playerId) || false;
  };

  if (variant === 'list') {
    return (
      <>
      <WaitlistDnDPanel enabled={hostSessionRosterDnDEnabled}>
        <ul className="grid grid-cols-1 gap-y-1 md:grid-cols-2 md:gap-x-6">
          {players.map((playerId) => {
            const player = playersById[playerId];
            if (!player) return null;
            const st = sessionPlayerStatus[player.id];
            const isPaused = st === 'paused';
            const isActiveWaiting = st === 'active';
            const waitPriority = getWaitPriority(player);
            const performanceBadge = getPerformanceBadge(player);
            const isOnCooldown = playerWaitMetrics[player.id]?.isOnCooldown ?? false;
            const isScheduled = sessionPlayerScheduledGames[player.id];
            const partnershipLock = partnershipLocks[player.id];

            const badges = [];
            if (cooldownEnabled && isOnCooldown) badges.push({ icon: '🧊', title: 'On cooldown — just finished a game' });
            if (isFairnessBlocked(player)) badges.push({ icon: '⚖️', title: `Fairness hold — ${gamesMode}+ games ahead of session minimum` });
            if (waitPriority === 'UP_SOON') badges.push({ icon: '⏳', title: 'Up soon' });
            if (performanceBadge) badges.push({ icon: performanceBadge.icon, title: performanceBadge.tooltip });
            if (isScheduled) badges.push({ icon: '📅', title: 'Reserved for scheduled game' });
            if (partnershipLock) badges.push({ icon: '🤝', title: `Waiting for partner` });

            const showPauseSlot =
              (onPausePlayer && isActiveWaiting) || (onResumePlayer && isPaused) || isPaused;

            return (
              <li
                key={player.id}
                className={`flex min-h-11 items-center gap-2.5 rounded-md px-2.5 py-1.5 min-w-0 ${
                  highlightPlayerId === player.id
                    ? 'ring-2 ring-primary/60 bg-primary/5'
                    : ''
                } ${
                  isPaused
                    ? 'border border-warning/40 bg-warning-light/15 opacity-90'
                    : 'hover:bg-surface-subtle'
                }`}
              >
                <QueuePositionMark position={getQueuePosition(playerId)} />
                <span
                  className={`h-8 w-8 flex-shrink-0 rounded-full text-xs font-bold flex items-center justify-center text-white ${getGenderAvatarBg(player.gender)}`}
                >
                  {getInitials(player.name)}
                </span>
                <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                  <WaitlistStatsTooltipName
                    player={player}
                    useSinglesElo={useSinglesElo}
                    waitMetricSlice={playerWaitMetrics[player.id] || null}
                    showEloTooltip={showEloTooltip}
                    className={`min-w-0 truncate text-base font-semibold leading-tight ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
                  />
                  {badges.length > 0 && (
                    <span className="flex shrink-0 items-center gap-0.5 text-sm leading-none">
                      {badges.map((b, i) => (
                        <span key={i} title={b.title} aria-hidden>{b.icon}</span>
                      ))}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="text-xs tabular-nums text-text-muted whitespace-nowrap">
                    <span className="text-text-muted/80">G:</span>
                    {formatSessionGamesCountLabel(player)}
                  </span>
                  {showHostOverflowMenu && hostActionContext ? (
                    <CompetitivePlayerOverflowMenu
                      player={player}
                      hostActionContext={hostActionContext}
                      excludeKeys={hostOverflowExcludeKeys}
                      disabled={busyPlayerId !== null}
                      menuOpenPlayerId={hostMenuOpenPlayerId}
                      onMenuOpenPlayerIdChange={onHostMenuOpenPlayerIdChange}
                    />
                  ) : (
                    <>
                      {showPauseSlot && (
                        <span className="flex shrink-0 items-center">
                          {onPausePlayer && isActiveWaiting && (
                            <button
                              type="button"
                              title={`Pause ${player.name}`}
                              aria-label={`Pause ${player.name}`}
                              disabled={busyPlayerId !== null}
                              onClick={() => runPauseAction(onPausePlayer, player.id)}
                              className="flex h-5 w-5 items-center justify-center rounded-full border border-border-medium bg-surface text-text-muted transition hover:border-warning/60 hover:bg-warning-light/30 hover:text-warning-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                            >
                              {busyPlayerId === player.id ? (
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                              ) : (
                                <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                              )}
                            </button>
                          )}
                          {onResumePlayer && isPaused && (
                            <button
                              type="button"
                              title={`Resume ${player.name}`}
                              aria-label={`Resume ${player.name}`}
                              disabled={busyPlayerId !== null}
                              onClick={() => runPauseAction(onResumePlayer, player.id)}
                              className="flex h-5 w-5 items-center justify-center rounded-full border border-warning bg-warning-light text-warning-dark transition hover:bg-warning-light/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning disabled:opacity-50"
                            >
                              {busyPlayerId === player.id ? (
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                              ) : (
                                <svg className="h-2.5 w-2.5 translate-x-[1px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path d="M8 5v14l11-7L8 5z" />
                                </svg>
                              )}
                            </button>
                          )}
                          {!onResumePlayer && isPaused && (
                            <span className="text-warning-dark text-xs" title="Paused" aria-hidden>⏸</span>
                          )}
                          {renderRemoveButton(player)}
                        </span>
                      )}
                      {!showPauseSlot && renderRemoveButton(player)}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </WaitlistDnDPanel>
      {removeConfirmModal}
      </>
    );
  }

  if (variant === 'chips') {
    return (
      <>
      <WaitlistDnDPanel enabled={hostSessionRosterDnDEnabled}>
        <div className="flex flex-wrap gap-1.5 py-1">
          {players.map((playerId) => {
            const player = playersById[playerId];
            if (!player) return null;
            const st = sessionPlayerStatus[player.id];
            const isPaused = st === 'paused';
            const isActiveWaiting = st === 'active';

            const waitPriority = getWaitPriority(player);
            const performanceBadge = getPerformanceBadge(player);
            const isOnCooldown = playerWaitMetrics[player.id]?.isOnCooldown ?? false;
            const isScheduled = sessionPlayerScheduledGames[player.id];
            const partnershipLock = partnershipLocks[player.id];

            const chipMatchPreference = playerWaitMetrics[player.id]?.matchPreference || 'no_preference';
            const chipPreferenceStrength = playerWaitMetrics[player.id]?.preferenceStrength || 'soft';
            const chipHardPrefLongWait =
              matchPreferencesEnabled &&
              chipPreferenceStrength === 'hard' &&
              chipMatchPreference !== 'no_preference' &&
              (playerWaitMetrics[player.id]?.courtsObserved || 0) >= MATCHMAKING_CONFIG.hardPreference.longWaitThreshold;

            const chipStatusBadges = [];
            if (chipHardPrefLongWait) {
              const expl = hardPreferenceLongWaitExplanation(chipMatchPreference);
              chipStatusBadges.push(
                <span
                  key="hard-pref-wait"
                  className="cursor-help leading-none"
                  title={expl}
                  aria-label={expl}
                  role="img"
                >
                  ⚠️
                </span>
              );
            }
            if (cooldownEnabled && isOnCooldown) {
              chipStatusBadges.push(
                <span key="cooldown" className="leading-none" title="On cooldown — just finished a game" aria-hidden>
                  🧊
                </span>
              );
            }
            if (isFairnessBlocked(player)) {
              chipStatusBadges.push(
                <span
                  key="fairness-hold"
                  className="leading-none"
                  title={`Fairness hold — ${gamesMode}+ games ahead of session minimum`}
                  aria-hidden
                >
                  ⚖️
                </span>
              );
            }
            if (waitPriority === 'UP_SOON') {
              chipStatusBadges.push(
                <span key="priority" className="leading-none" title="Up soon" aria-hidden>
                  ⏳
                </span>
              );
            }
            if (performanceBadge) {
              chipStatusBadges.push(
                <span key="perf" className="leading-none" title={performanceBadge.tooltip} aria-hidden>
                  {performanceBadge.icon}
                </span>
              );
            }
            if (isScheduled) {
              chipStatusBadges.push(
                <span key="sched" className="leading-none" title="Reserved for scheduled game" aria-hidden>
                  📅
                </span>
              );
            }
            if (partnershipLock) {
              const partner = playersById[partnershipLock.partnerId];
              const partnerName =
                partner?.name || [partner?.first_name, partner?.last_name].filter(Boolean).join(' ') || 'partner';
              chipStatusBadges.push(
                <span
                  key="partner"
                  className="leading-none"
                  title={partnershipLock.reason || `Waiting for partner ${partnerName}`}
                  aria-hidden
                >
                  🤝
                </span>
              );
            }

            const runPauseAction = async (fn, id) => {
              if (!fn || busyPlayerId) return;
              setBusyPlayerId(id);
              try { await fn(id); } finally { setBusyPlayerId(null); }
            };

            return (
              <WaitlistPlayerDraggable
                key={player.id}
                playerId={player.id}
                enabled={hostSessionRosterDnDEnabled && isActiveWaiting}
              >
                {({ outerRef, style, dragHandleProps }) => (
                  <div
                    ref={outerRef}
                    style={style}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 select-none transition-all duration-150 ${
                      highlightPlayerId === player.id ? 'ring-2 ring-primary/60 border-primary/40' : ''
                    } ${
                      isPaused
                        ? 'border-warning/50 bg-warning-light/20 text-warning-dark'
                        : `border-border-medium bg-surface hover:border-border hover:shadow-sm ${isScheduled ? 'opacity-60' : ''}`
                    } ${
                      hostSessionRosterDnDEnabled && isActiveWaiting
                        ? 'cursor-grab active:cursor-grabbing touch-manipulation'
                        : ''
                    }`}
                    {...(dragHandleProps || {})}
                  >
                    <QueuePositionMark position={getQueuePosition(playerId)} className="w-4 text-[11px]" />
                    <WaitlistStatsTooltipName
                      player={player}
                      useSinglesElo={useSinglesElo}
                      waitMetricSlice={playerWaitMetrics[player.id] || null}
                      showEloTooltip={showEloTooltip}
                      className={`max-w-[18rem] truncate text-lg font-semibold leading-tight ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
                    />
                    {chipStatusBadges.length > 0 && (
                      <span
                        className="flex shrink-0 items-center gap-0.5 text-base leading-none tracking-tight"
                        aria-label="Status indicators"
                      >
                        {chipStatusBadges.slice(0, 3)}
                      </span>
                    )}
                    <span className="shrink-0 text-sm tabular-nums text-text-muted">
                      {formatSessionGamesCountLabel(player)}
                    </span>
                    {onPausePlayer && isActiveWaiting && (
                      <button
                        type="button"
                        title={`Pause ${player.name}`}
                        aria-label={`Pause ${player.name}`}
                        disabled={busyPlayerId !== null}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => runPauseAction(onPausePlayer, player.id)}
                        className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border-medium bg-surface text-text-muted transition hover:border-warning/60 hover:bg-warning-light/30 hover:text-warning-dark focus:outline-none disabled:opacity-50"
                      >
                        {busyPlayerId === player.id ? (
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                        ) : (
                          <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {onResumePlayer && isPaused && (
                      <button
                        type="button"
                        title={`Resume ${player.name}`}
                        aria-label={`Resume ${player.name}`}
                        disabled={busyPlayerId !== null}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => runPauseAction(onResumePlayer, player.id)}
                        className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-warning bg-warning-light text-warning-dark transition hover:bg-warning-light/80 focus:outline-none disabled:opacity-50"
                      >
                        {busyPlayerId === player.id ? (
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                        ) : (
                          <svg className="h-2.5 w-2.5 translate-x-[1px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path d="M8 5v14l11-7L8 5z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {!onResumePlayer && isPaused && (
                      <span className="ml-0.5 shrink-0 text-warning-dark" title="Paused" aria-hidden>⏸</span>
                    )}
                    {renderRemoveButton(player, { compact: true })}
                  </div>
                )}
              </WaitlistPlayerDraggable>
            );
          })}
        </div>
      </WaitlistDnDPanel>
      {removeConfirmModal}
      </>
    );
  }

  return (
    <>
    <div className={wrapperClassName}>
      {/* auto-fill + min track width: one comfortable column in narrow sidebars; many columns on wide layouts */}
      <WaitlistDnDPanel enabled={hostSessionRosterDnDEnabled}>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(13.5rem,1fr))]">
        {players.map(playerId => {
          const player = playersById[playerId];
          if (!player) return null;

          // Get indicators
          const waitPriority = getWaitPriority(player);
          const courtsObserved = playerWaitMetrics[player.id]?.courtsObserved || 0;
          const performanceBadge = getPerformanceBadge(player);
          const isDeferred = isDeferredPlayer(player.id);
          
          // Check if player is on cooldown
          // READ directly from database - no calculation needed!
          // The normalized state table has authoritative is_on_cooldown field
          const isOnCooldown = playerWaitMetrics[player.id]?.isOnCooldown ?? false;
          
          // Build badges array (positioned at top-left)
          const badges = [];
          
          // Cooldown badge (show first as it's a temporary state)
          if (cooldownEnabled && isOnCooldown) {
            badges.push(
              <span key="cooldown" className="leading-none" title="On cooldown - just finished a game">🧊</span>
            );
          }

          if (isFairnessBlocked(player)) {
            badges.push(
              <span
                key="fairness-hold"
                className="leading-none"
                title={`Fairness hold — ${gamesMode}+ games ahead of session minimum`}
              >
                ⚖️
              </span>
            );
          }
          
          // Wait priority badge
          if (waitPriority === 'UP_SOON') {
            badges.push(
              <span key="priority" className="leading-none" title="Up Soon">⏳</span>
            );
          }
          
          // Performance badges
          if (performanceBadge) {
            badges.push(
              <span key="performance" className="leading-none" title={performanceBadge.tooltip}>{performanceBadge.icon}</span>
            );
          }
          
          // Strategy (deferred) badge
          if (isDeferred) {
            badges.push(
              <span key="deferred" className="leading-none" title="Waiting for better matchup">🎯</span>
            );
          }
          
          const isScheduled = sessionPlayerScheduledGames[player.id];

          if (isScheduled) {
            badges.push(
              <span key="scheduled" className="leading-none" title="Reserved for scheduled game">📅</span>
            );
          }

          const partnershipLock = partnershipLocks[player.id];
          if (partnershipLock) {
            const partner = playersById[partnershipLock.partnerId];
            const partnerName = partner?.name || [partner?.first_name, partner?.last_name].filter(Boolean).join(' ') || 'partner';
            badges.push(
              <span
                key="partnership"
                className="leading-none"
                title={partnershipLock.reason || `Waiting for partner ${partnerName}`}
              >
                🤝
              </span>
            );
          }

          // Hard preference long-wait warning
          const matchPreference = playerWaitMetrics[player.id]?.matchPreference || 'no_preference';
          const preferenceStrength = playerWaitMetrics[player.id]?.preferenceStrength || 'soft';
          const hardPrefLongWait =
            matchPreferencesEnabled &&
            preferenceStrength === 'hard' &&
            matchPreference !== 'no_preference' &&
            courtsObserved >= MATCHMAKING_CONFIG.hardPreference.longWaitThreshold;
          if (hardPrefLongWait) {
            const expl = hardPreferenceLongWaitExplanation(matchPreference);
            badges.push(
              <span
                key="hard-pref-wait"
                className="cursor-help leading-none"
                title={expl}
                aria-label={expl}
                role="img"
              >
                ⚠️
              </span>
            );
          }

          const st = sessionPlayerStatus[player.id];
          const isPaused = st === 'paused';
          const isActiveWaiting = st === 'active';

          const runPauseAction = async (fn, id) => {
            if (!fn || busyPlayerId) return;
            setBusyPlayerId(id);
            try {
              await fn(id);
            } finally {
              setBusyPlayerId(null);
            }
          };

          const showPauseSlot = (onPausePlayer && isActiveWaiting) || isPaused;

          return (
            <WaitlistPlayerDraggable
              key={player.id}
              playerId={player.id}
              enabled={hostSessionRosterDnDEnabled && isActiveWaiting}
            >
              {({ outerRef, style, dragHandleProps }) => (
            <div 
              ref={outerRef}
              style={style}
              className={`group relative rounded-lg shadow-sm border p-3 min-h-[88px] transition-all duration-200 select-none ${
                isPaused
                  ? 'border-warning/60 bg-warning-light/15 ring-2 ring-warning/40 hover:border-warning hover:shadow-md'
                  : `bg-surface border-border-medium hover:shadow-md hover:border-border ${isScheduled ? 'opacity-60 bg-surface-subtle' : ''}`
              }${
                hostSessionRosterDnDEnabled && isActiveWaiting
                  ? ' cursor-grab active:cursor-grabbing touch-manipulation'
                  : ''
              }`}
              {...(dragHandleProps || {})}
            >
              {/* Name row: player name → status badges inline → pause */}
              <div className="mb-1 flex min-w-0 items-start gap-1.5">
                <QueuePositionMark position={getQueuePosition(player.id)} className="w-5 pt-1 text-sm" />
                <div className="min-w-0 flex-1">
                  <WaitlistStatsTooltipName
                    player={player}
                    useSinglesElo={useSinglesElo}
                    waitMetricSlice={playerWaitMetrics[player.id] || null}
                    showEloTooltip={showEloTooltip}
                    className={`block min-w-0 truncate text-lg font-semibold ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
                  />
                </div>
                {badges.length > 0 && (
                  <div
                    className="flex shrink-0 flex-wrap items-center gap-1 pt-0.5"
                    aria-label="Player status indicators"
                  >
                    {badges}
                  </div>
                )}
                {showPauseSlot && (
                  <div className="flex shrink-0 flex-col items-end justify-start gap-1">
                    {onPausePlayer && isActiveWaiting && (
                      <button
                        type="button"
                        title="Pause — remove from matchmaking until resumed"
                        aria-label={`Pause ${player.name}`}
                        disabled={busyPlayerId !== null}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => runPauseAction(onPausePlayer, player.id)}
                        className="flex h-5 w-5 items-center justify-center rounded-full border border-border-medium bg-surface text-text-secondary shadow-sm transition hover:border-warning/60 hover:bg-warning-light/30 hover:text-warning-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                      >
                        {busyPlayerId === player.id ? (
                          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                        ) : (
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {onResumePlayer && isPaused && (
                      <button
                        type="button"
                        title="Resume — back in the queue for matchmaking"
                        aria-label={`Resume ${player.name}`}
                        disabled={busyPlayerId !== null}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => runPauseAction(onResumePlayer, player.id)}
                        className="flex h-5 w-5 items-center justify-center rounded-full border border-warning bg-warning-light text-warning-dark shadow-sm transition hover:bg-warning-light/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning disabled:opacity-50"
                      >
                        {busyPlayerId === player.id ? (
                          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-warning-dark" aria-hidden />
                        ) : (
                          <svg className="h-3 w-3 translate-x-[1px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path d="M8 5v14l11-7L8 5z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {!onResumePlayer && isPaused && (
                      <div
                        className="flex h-5 w-5 items-center justify-center rounded-full border border-warning bg-warning-light shadow-sm"
                        title="Paused"
                      >
                        <svg className="h-3 w-3 text-warning-dark" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      </div>
                    )}
                    {renderRemoveButton(player)}
                  </div>
                )}
                {!showPauseSlot && canRemoveFromSession(player) && (
                  <div className="flex shrink-0 flex-col items-end justify-start gap-1">
                    {renderRemoveButton(player)}
                  </div>
                )}
              </div>

              {isPaused && (
                <div className="mb-2">
                  <span className="inline-flex rounded-md bg-warning-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-dark">
                    Paused — not in matchmaking
                  </span>
                </div>
              )}

              {/* Games played and court counter */}
              <div className="flex items-center justify-between">
                <div>
                  {player.effectiveGamesPlayed !== undefined && player.effectiveGamesPlayed !== (player.gamesPlayedThisSession || 0) ? (
                    <span className="text-xs text-danger-dark font-semibold">
                      Games: {player.effectiveGamesPlayed} (Late)
                    </span>
                  ) : (
                    <span className="text-xs text-text-muted">
                      Games: {player.gamesPlayedThisSession || 0}
                    </span>
                  )}
                </div>
                
                {/* Wait counter */}
                {courtsObserved >= 0 && (
                  <span className="text-xs text-info-dark font-medium bg-info-light px-2 py-0.5 rounded-full" title="Wait time (courts observed while waiting)">
                    Wait: {courtsObserved}
                  </span>
                )}
              </div>
            </div>
              )}
            </WaitlistPlayerDraggable>
          );
        })}
      </div>
      </WaitlistDnDPanel>
    </div>
    {removeConfirmModal}
    </>
  );
};

export default WaitingList; 